import { useContext, useEffect, useMemo, useState } from 'react';

import { ArrowLeftOutlined } from '@ant-design/icons';
import {
	Button,
	Descriptions,
	Flex,
	Popconfirm,
	Space,
	Spin,
	Tag,
	Tooltip,
	Typography,
	message,
} from 'antd';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
// import {  UsergroupAddOutlined } from "@ant-design/icons";
import { useNavigate } from 'react-router-dom';

import { SubscriptionContext } from '@/App';
import { useGetCreatorById } from '@/api/creators';
import { SUBSCRIPTIONS_TYPES_ENUM } from '@/const';
import defaultAvatarImg from '@/lib/assets/default-avatar.jpg';
import { formatNumber } from '@/lib/helpers/formatNumber';
import { formatSecondsToTime } from '@/lib/helpers/formatSecondsToTime';
import getPlatformFromUrl from '@/lib/helpers/getPlatformFromUrl';
import hasSubscriptionType from '@/lib/helpers/hasSubscriptionType';
import prepareImageSrc from '@/lib/helpers/prepareImageSrc';
import { PageContext } from '@/providers/PageContextProvider';
import { ICategoryCreator, IResponseError } from '@/ts';

import styles from './styles.module.css';

import Platforms from '../../../creators/platforms';
import CreatorsContent from './CreatorsContent';

function Creator() {
	const { id, categoryId } = useParams();
	const navigate = useNavigate();
	const { setPageTitle, setIsCreditsCounterVisible } = useContext(PageContext);
	const { subscriptions, subscriptionLoading } =
		useContext(SubscriptionContext);
	const { data, isLoading, isFetching } = useGetCreatorById(id!);
	const creatorItem = useMemo(() => data?.data?.data?.item, [data]);

	const [src, setSrc] = useState<string | undefined>('');

	useEffect(() => {
		if (!isLoading && !isFetching && creatorItem?.profileImageURL) {
			setSrc(prepareImageSrc(creatorItem?.profileImageURL) || defaultAvatarImg);
		}
	}, [isLoading, isFetching]);

	useEffect(() => {
		if (!subscriptionLoading && subscriptions && subscriptions?.length > 0) {
			if (
				!hasSubscriptionType(subscriptions, SUBSCRIPTIONS_TYPES_ENUM.datalake)
			) {
				navigate('/app/dashboard/product-main-menu');
			}
		} else if (
			!subscriptionLoading &&
			subscriptions &&
			subscriptions?.length === 0
		) {
			navigate('/app/dashboard/product-main-menu');
		}
	}, [subscriptions, subscriptionLoading]);

	useEffect(() => {
		setPageTitle('Video Intelligence Panel');
		setIsCreditsCounterVisible(false);

		return () => {
			setPageTitle('');
			setIsCreditsCounterVisible(true);
		};
	}, []);

	const platforms = creatorItem?.platforms
		? Object.entries(creatorItem?.platforms).reduce(
				(acc: { platform: string; url: string }[], [key, value]) => {
					if (value !== null) {
						acc.push({ platform: key, url: value?.url });
					}
					return acc;
				},
				[],
		  )
		: [];

	const followersDataArr = creatorItem?.platforms
		? Object.entries(creatorItem?.platforms)?.reduce(
				(
					acc: {
						platform: string;
						subscribers?: string;
						followers?: string;
					}[],
					[key, value],
				) => {
					if (
						value !== null &&
						(value?.connections?.subscribers || value?.connections?.followers)
					) {
						acc.push({
							platform: key,
							subscribers:
								value?.connections?.subscribers ??
								value?.connections?.followers,
						});
					}

					return acc;
				},
				[],
		  )
		: [];

	const activePlatform = creatorItem?.platforms
		? (Object.entries(creatorItem?.platforms).reduce(
				(acc: string, [key, value]) => {
					if (value !== null) {
						acc = key;
					}
					return acc;
				},
				'',
		  ) as keyof ICategoryCreator['platforms'])
		: null;

	const platformFromUrl = creatorItem?.platformHandles?.[0];

	return (
		<>
			<Flex justify="space-between" align="center" style={{ marginBottom: 20 }}>
				<Typography.Title level={4}>Creator</Typography.Title>

				<Button
					onClick={() => {
						const lsPage = localStorage.getItem('category_page') || 1;
						navigate(
							`/app/dashboard/video-intelligence-lake/${categoryId}?page=${lsPage}`,
						);
					}}
				>
					<ArrowLeftOutlined /> Back
				</Button>
			</Flex>

			{isLoading || isFetching ? (
				<div className={styles.loaderWrapper}>
					<Spin />
				</div>
			) : (
				<>
					<Flex justify="space-between" style={{ width: '100%' }}>
						<Typography.Title
							style={{
								display: 'flex',
								alignItems: 'center',
								fontSize: '16px',
								fontWeight: 600,
							}}
						>
							{creatorItem?.profileName}
						</Typography.Title>
					</Flex>
					<div className={styles.metaWrapper}>
						<div>
							<Descriptions
								bordered
								//   title='General'
								size={'small'}
								column={1}
								style={{ marginBottom: '1.5rem' }}
							>
								<Descriptions.Item label="Profile link">
									<Flex gap={8}>
										{activePlatform &&
										creatorItem?.platforms?.[activePlatform]?.url ? (
											<Link
												key={creatorItem?.platforms?.[activePlatform]?.url}
												to={creatorItem?.platforms?.[activePlatform]?.url!}
												target="_blank"
												style={{ display: 'flex', alignItems: 'center' }}
											>
												{creatorItem?.platforms?.[activePlatform]?.url}
											</Link>
										) : platformFromUrl ? (
											<Link
												key={platformFromUrl}
												to={platformFromUrl}
												target="_blank"
												style={{ display: 'flex', alignItems: 'center' }}
											>
												{platformFromUrl}
											</Link>
										) : (
											creatorItem?.profileName ?? '-'
										)}

										{/* {creatorItem?.associatedIds &&
                      creatorItem?.associatedIds?.length > 0 && (
                        <Tooltip title={`This account is grouped`}>
                          <UsergroupAddOutlined />
                        </Tooltip>
                      )} */}
									</Flex>
								</Descriptions.Item>

								<Descriptions.Item label="Language">
									{creatorItem?.language || '-'}
								</Descriptions.Item>

								<Descriptions.Item label="Platform">
									<div style={{ display: 'flex', gap: '0.375rem' }}>
										{platforms?.length > 0 ? (
											platforms?.map((item) => (
												<Link
													key={item?.platform}
													to={item?.url}
													target="_blank"
													title={item?.platform}
													style={{ display: 'flex', alignItems: 'center' }}
												>
													{item?.platform && (
														<Platforms platform={item?.platform} />
													)}
												</Link>
											))
										) : (
											<>
												{platformFromUrl ? (
													<Platforms
														platform={
															getPlatformFromUrl(platformFromUrl!)?.name!
														}
													/>
												) : (
													'-'
												)}
											</>
										)}
									</div>
								</Descriptions.Item>

								{activePlatform && (
									<Descriptions.Item label="Description">
										{creatorItem?.platforms?.[activePlatform]?.description}
									</Descriptions.Item>
								)}

								<Descriptions.Item label="Main categories">
									<Space size={[0, 8]} wrap>
										{creatorItem?.svClassification?.map((c: any) => (
											<Tag key={c}>{c?.split('>')?.join(' > ')}</Tag>
										))}
									</Space>
								</Descriptions.Item>

								<Descriptions.Item label="Followers">
									{followersDataArr?.length > 0
										? followersDataArr.map(({ platform, subscribers }) => (
												<span key={platform}>
													{subscribers && formatNumber(subscribers)}
												</span>
										  ))
										: '-'}
								</Descriptions.Item>

								{!!activePlatform && (
									<Descriptions.Item label="Posts">
										<Space size={[0, 8]} wrap>
											{creatorItem?.platforms?.[activePlatform]?.posts
												? Object.entries(
														creatorItem?.platforms?.[activePlatform]
															?.posts as any,
												  ).map(
														([key, value]: [
															key: string | unknown,
															value: string | unknown,
														]) => (
															<Tag key={key as string}>
																<b style={{ textTransform: 'capitalize' }}>
																	{key as string}:
																</b>{' '}
																{formatNumber(value as string)}
															</Tag>
														),
												  )
												: null}
										</Space>
									</Descriptions.Item>
								)}

								{!!activePlatform && (
									<Descriptions.Item label="Stats">
										<Space size={[0, 8]} wrap>
											{creatorItem?.platforms?.[activePlatform]?.stats
												? Object.entries(
														creatorItem?.platforms?.[activePlatform]
															?.stats as any,
												  ).map(
														([key, value]: [
															key: string | unknown,
															value: string | unknown,
														]) => (
															<Tag key={key as string}>
																<b style={{ textTransform: 'capitalize' }}>
																	{key as string}:
																</b>{' '}
																{formatNumber(value as string)}
															</Tag>
														),
												  )
												: null}
										</Space>
									</Descriptions.Item>
								)}

								{creatorItem?.videoQuantity! >= 0 && (
									<Descriptions.Item label="Content quantity">
										{creatorItem?.videoQuantity}
									</Descriptions.Item>
								)}

								{creatorItem?.videoDuration! >= 0 && (
									<Descriptions.Item label="Content duration">
										{formatSecondsToTime(
											creatorItem?.videoDuration as number,
										) || 0}
									</Descriptions.Item>
								)}
							</Descriptions>
						</div>

						<div className={styles.imageWrapper}>
							{src && (
								<img
									width={300}
									height={300}
									className={styles.image}
									src={src}
									onError={() => {
										setSrc(defaultAvatarImg);
									}}
									alt={`${creatorItem?.profileName} profile picture`}
								/>
							)}
						</div>
					</div>

					{id && <CreatorsContent id={id} />}
					{/* <Button type='primary' disabled>
            Update
          </Button> */}
				</>
			)}
		</>
	);
}

export default Creator;
